/* src/hooks/useSolicitudesVacaciones.ts */
import { useState, useCallback } from "react";
import api from "../api/axios";
import { IsoDate, Tramo } from "./useVacaciones";

/* —— tipos ————————————————————————— */
export type EstadoSV = "pendiente" | "aprobada" | "rechazada";

export interface SolicitudVacacion {
  id: number;
  fecha: IsoDate;
  tramo: Tramo;
  estado: EstadoSV;
  idProfesor?: number;
}

interface NuevaSolicitud {
  fecha: IsoDate;
  tramo: Tramo;
}

/* —— hook ————————————————————————— */
export function useSolicitudesVacaciones(idProfesor?: number) {
  const [rows, setRows] = useState<SolicitudVacacion[]>([]);
  const [loading, setLoading] = useState(false);

  /* ---- READ (solo pendientes) ------------------------------ */
  const refresh = useCallback(async () => {
    if (!idProfesor) {
      // sin user → lista vacía
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get<SolicitudVacacion[]>(
        "/solicitudes-vacaciones",
        { params: { estado: "pendiente", profId: idProfesor } } // ← FIX aquí
      );
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [idProfesor]);

  /* ---- CREATE (optimistic) --------------------------------- */
  const solicitar = useCallback(
    (vacs: NuevaSolicitud[]) => {
      // 1) Optimista: pintamos filas pendientes con id temporal
      setRows((r) =>
        r.concat(
          vacs.map((v, i) => ({
            id: Date.now() * -1 - i, // id negativo temporal
            fecha: v.fecha,
            tramo: v.tramo,
            estado: "pendiente" as const,
            idProfesor,
          }))
        )
      );

      // 2) Llamada real y sincronización
      return api.post("/solicitudes-vacaciones", vacs).then(refresh);
    },
    [idProfesor, refresh]
  );

  /* ---- DELETE (cancelar) ----------------------------------- */
  const cancelar = useCallback(
    (id: number) => api.delete(`/solicitudes-vacaciones/${id}`).then(refresh),
    [refresh]
  );

  /* ---- APPROVE (HR / admin) -------------------------------- */
  const aprobar = useCallback(
    (id: number) => api.patch(`/solicitudes-vacaciones/${id}/aprobar`).then(refresh),
    [refresh]
  );

  /* ---- REJECT (HR / admin) --------------------------------- */
  const rechazar = useCallback(
    (id: number, motivo?: string) =>
      api.patch(`/solicitudes-vacaciones/${id}/rechazar`, { motivo }).then(refresh),
    [refresh]
  );

  return {
    rows,
    loading,
    refresh,
    solicitar,
    cancelar,
    aprobar,
    rechazar,
  };
}
